import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

const SERVICE_NAME = '안심센트럴';

const VARIANT_TAGLINE = {
  central: '통합관제 시스템',
  monitor: '통합 관제 모니터링',
  individual: '개별 관제 모니터링',
} as const;

export type KymaBrandVariant = keyof typeof VARIANT_TAGLINE;

export type KymaBrandTitleProps = {
  variant?: KymaBrandVariant;
  /** false면 안심센트럴 | KYMA만 표시 (현장 관리 등) */
  showTagline?: boolean;
  /** AppBar(밝은 글자) vs 일반 배경 */
  tone?: 'onPrimary' | 'default';
  size?: 'large' | 'medium' | 'compact';
  /** 로그인 등 카드 중앙 정렬 */
  align?: 'start' | 'center';
  sx?: SxProps<Theme>;
};

const KymaBrandTitle: React.FC<KymaBrandTitleProps> = ({
  variant = 'central',
  showTagline = true,
  tone = 'onPrimary',
  size = 'large',
  align = 'start',
  sx,
}) => {
  const tagline = VARIANT_TAGLINE[variant];
  const onPrimary = tone === 'onPrimary';

  const kymaColor = 'primary.main';
  const serviceColor = 'text.primary';
  const accentBg = 'primary.main';
  const dividerColor = 'divider';

  const serviceFontSize =
    size === 'large'
      ? { xs: '0.9375rem', sm: '1.15rem', md: '1.35rem' }
      : size === 'medium'
        ? { xs: '0.9375rem', sm: '1.05rem' }
        : { xs: '0.8125rem', sm: '0.875rem' };

  const kymaFontSize =
    size === 'large'
      ? { xs: '1.125rem', sm: '1.35rem', md: '1.65rem' }
      : size === 'medium'
        ? { xs: '1.05rem', sm: '1.25rem' }
        : { xs: '0.9rem', sm: '1rem' };

  const accentHeight =
    size === 'large' ? { xs: 22, sm: 28, md: 30 } : size === 'medium' ? 24 : 20;

  const taglineFontSize =
    size === 'large'
      ? { xs: '0.8125rem', sm: '1.05rem', md: '1.2rem' }
      : size === 'medium'
        ? { xs: '0.8125rem', sm: '0.9375rem' }
        : { xs: '0.75rem', sm: '0.8125rem' };

  /** KYMA와 태그라인 사이(태그라인 글자 앞) 여백 */
  const taglineBeforeSpacing =
    size === 'large'
      ? { xs: 1.5, sm: 2.5, md: 3 }
      : size === 'medium'
        ? { xs: 1.5, sm: 1.5 }
        : { xs: 1, sm: 1.25 };

  const taglineDividerMx =
    size === 'large' ? { sm: 1.25, md: 1.5 } : { sm: 0.75 };

  const centered = align === 'center';

  return (
    <Box
      component="div"
      role="group"
      aria-label={
        showTagline
          ? `${SERVICE_NAME} KYMA ${tagline}`
          : `${SERVICE_NAME} KYMA`
      }
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: centered
          ? 'center'
          : { xs: 'flex-start', sm: 'center' },
        gap: { xs: 0.25, sm: 1.25 },
        minWidth: 0,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, flexShrink: 0 }}>
        <Box
          aria-hidden
          sx={{
            width: 3,
            height: accentHeight,
            borderRadius: 1,
            bgcolor: accentBg,
            boxShadow: onPrimary ? '0 0 10px rgba(0, 119, 182, 0.35)' : 'none',
            flexShrink: 0,
          }}
        />
        <Typography
          component="span"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.02em',
            fontSize: serviceFontSize,
            lineHeight: 1.1,
            color: serviceColor,
            opacity: onPrimary ? 0.92 : 1,
            whiteSpace: { sm: 'nowrap' },
          }}
        >
          {SERVICE_NAME}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          pl: centered ? 0 : { xs: 1.5, sm: 0 },
          gap: { xs: 0.5, sm: 0.75 },
        }}
      >
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            display: { xs: 'none', sm: 'block' },
            alignSelf: 'stretch',
            my: 0.35,
            mx: 0.25,
            borderColor: dividerColor,
          }}
        />
        <Typography
          component="span"
          aria-hidden
          sx={{
            display: { xs: 'inline', sm: 'none' },
            fontWeight: 300,
            color: 'text.secondary',
            lineHeight: 1,
          }}
        >
          |
        </Typography>
        <Typography
          component="span"
          sx={{
            fontWeight: 800,
            letterSpacing: '0.14em',
            fontSize: kymaFontSize,
            lineHeight: 1.1,
            color: kymaColor,
            fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
          }}
        >
          KYMA
        </Typography>
      </Box>

      {showTagline ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            flexShrink: 0,
            ml: { xs: 0, sm: taglineBeforeSpacing },
            pl: centered ? 0 : { xs: 1.5, sm: 0 },
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              display: { xs: 'none', sm: 'block' },
              alignSelf: 'stretch',
              my: 0.35,
              mx: taglineDividerMx,
              borderColor: dividerColor,
            }}
          />
          <Typography
            component="span"
            sx={{
              fontWeight: 500,
              letterSpacing: '-0.02em',
              fontSize: taglineFontSize,
              lineHeight: 1.25,
              color: serviceColor,
              opacity: onPrimary ? 0.92 : 1,
              whiteSpace: { sm: 'nowrap' },
            }}
          >
            {tagline}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default KymaBrandTitle;
